import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Check, X as XIcon, GripVertical } from 'lucide-react';
import {
  getPublicPortfolio,
  unlockPortfolio,
  type PublicPortfolio,
  type PublicPortfolioImage,
} from '../api/portfolios';
import { reorderSetImages } from '../api/sets';
import { ApiError } from '../api/client';
import { Logo } from '../components/Logo';
import { track, trackPortfolio } from '../lib/analytics';
import { ageFromBirthday } from '../utils/age';
import { PortfolioLightbox } from './PortfolioLightbox';
import './portfolio-viewer.css';

// PortfolioViewer — public route /p/:slug. Renderar:
//   - locked-state med PIN-prompt
//   - expired-state med "request new link"
//   - not-found-state
//   - unlocked: full portfolio med header + primary set + secondary set
//
// Designen följer portfolio-prototype.html Variant E (Storytelling):
// 40/60 split hero (info | tall portrait), primary som cascade av
// full-width / 2-up frames, secondary som mindre band under.

export default function PortfolioViewer() {
  const { slug } = useParams<{ slug: string }>();

  const portfolioQuery = useQuery({
    queryKey: ['public-portfolio', slug],
    queryFn: () => {
      if (!slug) throw new Error('no slug');
      return getPublicPortfolio(slug);
    },
    enabled: !!slug,
    retry: false,
  });

  const data = portfolioQuery.data;

  // Track view när unlocked-data laddats — men SKIPPA om besökaren är ägare
  // (inloggad workspace-medlem som granskar sin egen portfolio).
  useEffect(() => {
    if (data?.kind === 'unlocked') {
      trackPortfolio(
        {
          name: 'portfolio_view',
          data: { portfolio_id: data.portfolio.id },
        },
        data.portfolio.isOwner,
      );
    }
  }, [data]);

  if (!slug) return <NotFoundView />;
  if (portfolioQuery.isLoading) return <LoadingView />;
  if (!data) return <NotFoundView />;

  if (data.kind === 'not-found') return <NotFoundView />;
  if (data.kind === 'expired') {
    return <ExpiredView name={data.portfolio.name} />;
  }
  if (data.kind === 'locked') {
    return <LockedView slug={slug} name={data.portfolio.name} onUnlock={() => portfolioQuery.refetch()} />;
  }

  return <UnlockedPortfolio portfolio={data.portfolio} />;
}

// ─── Loading ────────────────────────────────────────────────────────────

function LoadingView() {
  return (
    <div className="pv-shell pv-center">
      <div className="pv-loading">Loading portfolio…</div>
    </div>
  );
}

// ─── Not found ──────────────────────────────────────────────────────────

function NotFoundView() {
  return (
    <div className="pv-shell pv-center">
      <div className="pv-state-card">
        <div className="pv-state-icon">·</div>
        <h1>Portfolio not found</h1>
        <p>This link may have been removed or never existed.</p>
        <a href="/" className="pv-link">vuelette.com</a>
      </div>
    </div>
  );
}

// ─── Expired ────────────────────────────────────────────────────────────

function ExpiredView({ name }: { name: string }) {
  return (
    <div className="pv-shell pv-center">
      <div className="pv-state-card">
        <div className="pv-state-icon">·</div>
        <h1>This portfolio has expired</h1>
        <p>
          The link to <strong>{name}</strong> is no longer active. Please contact
          the sender to request a new one.
        </p>
        <a href="/" className="pv-link">vuelette.com</a>
      </div>
    </div>
  );
}

// ─── Locked (PIN prompt) ────────────────────────────────────────────────

function LockedView({
  slug,
  name,
  onUnlock,
}: {
  slug: string;
  name: string;
  onUnlock: () => void;
}) {
  const [pin, setPin] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const unlockMutation = useMutation({
    mutationFn: () => unlockPortfolio(slug, pin),
    onSuccess: () => {
      track({
        name: 'portfolio_view',
        data: { portfolio_id: slug },
      });
      onUnlock();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        setErrorMsg('Incorrect PIN');
      } else {
        setErrorMsg('Could not unlock. Please try again.');
      }
      setTimeout(() => setErrorMsg(null), 2500);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pin.length < 4 || pin.length > 8) {
      setErrorMsg('PIN must be 4-8 digits');
      return;
    }
    unlockMutation.mutate();
  }

  return (
    <div className="pv-shell pv-center">
      <div className="pv-state-card">
        <div className="pv-brand-mark">
          <Logo size="md" color="#ededed" />
        </div>
        <h1>This portfolio is private</h1>
        <p>
          <strong>{name}</strong> is password-protected. Enter the PIN you were given
          to view it.
        </p>
        <form onSubmit={handleSubmit} className="pv-pin-form">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="Enter PIN"
            disabled={unlockMutation.isPending}
            maxLength={8}
            autoFocus
            className="pv-pin-input"
          />
          <button
            type="submit"
            disabled={unlockMutation.isPending || pin.length < 4}
            className="pv-pin-button"
          >
            {unlockMutation.isPending ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>
        {errorMsg ? <div className="pv-error">{errorMsg}</div> : null}
      </div>
    </div>
  );
}

// ─── Unlocked: full portfolio ───────────────────────────────────────────

function UnlockedPortfolio({ portfolio }: { portfolio: PublicPortfolio }) {
  const queryClient = useQueryClient();
  const header = portfolio.headerData;
  const hasHeader = !!(
    header?.modelName ||
    header?.birthday ||
    header?.location ||
    header?.ethnicity ||
    header?.measurements ||
    header?.contact ||
    header?.intro
  );
  const age = ageFromBirthday(header?.birthday);

  // Edit mode — bara tillgängligt om isOwner. När aktiverad visas grip-handles
  // och drag-and-drop på bilderna.
  const [editMode, setEditMode] = useState(false);

  // Lokal ordningskopia för båda sets — vi muterar denna under drag och
  // skickar bulk-update när user klickar Save.
  const [primaryOrder, setPrimaryOrder] = useState<PublicPortfolioImage[]>(
    portfolio.primarySet.images,
  );
  const [secondaryOrder, setSecondaryOrder] = useState<PublicPortfolioImage[]>(
    portfolio.secondarySet?.images ?? [],
  );

  // Reset local-state när portfolio refreshar (efter save)
  useEffect(() => {
    setPrimaryOrder(portfolio.primarySet.images);
    setSecondaryOrder(portfolio.secondarySet?.images ?? []);
  }, [portfolio.primarySet.images, portfolio.secondarySet?.images]);

  const reorderMutation = useMutation({
    mutationFn: async () => {
      // Skicka båda sets om de ändrats
      const promises: Promise<unknown>[] = [];
      promises.push(reorderSetImages(portfolio.primarySet.id, primaryOrder.map((i) => i.id)));
      if (portfolio.secondarySet && secondaryOrder.length > 0) {
        promises.push(
          reorderSetImages(portfolio.secondarySet.id, secondaryOrder.map((i) => i.id)),
        );
      }
      await Promise.all(promises);
    },
    onSuccess: () => {
      // Refetch portfolio + invalidera set-listor i appen
      queryClient.invalidateQueries({ queryKey: ['public-portfolio', portfolio.slug] });
      queryClient.invalidateQueries({ queryKey: ['set', portfolio.primarySet.id] });
      if (portfolio.secondarySet) {
        queryClient.invalidateQueries({ queryKey: ['set', portfolio.secondarySet.id] });
      }
      setEditMode(false);
    },
  });

  function cancelEdit() {
    // Återställ till server-state
    setPrimaryOrder(portfolio.primarySet.images);
    setSecondaryOrder(portfolio.secondarySet?.images ?? []);
    setEditMode(false);
  }

  // Hero-image — första bilden från primary set (i nuvarande sort)
  const heroImage = primaryOrder[0];

  // Hela bildlistan för lightbox-navigation. Primary först, sedan secondary.
  const allImages = useMemo(() => {
    const imgs = [...primaryOrder];
    if (portfolio.secondarySet) imgs.push(...secondaryOrder);
    return imgs;
  }, [primaryOrder, secondaryOrder, portfolio.secondarySet]);

  // Lightbox state — null = stängd, number = open at index
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  function openLightbox(img: PublicPortfolioImage) {
    if (editMode) return; // I edit-mode klick = pickup för drag, inte lightbox
    const idx = allImages.findIndex((i) => i.id === img.id);
    setLightboxIndex(idx >= 0 ? idx : 0);
  }

  function handleImageView(img: PublicPortfolioImage) {
    trackPortfolio(
      {
        name: 'portfolio_image_view',
        data: { portfolio_id: portfolio.id, image_id: img.id },
      },
      portfolio.isOwner,
    );
  }

  function handleContactClick(channel: 'email' | 'phone' | 'instagram') {
    trackPortfolio(
      {
        name: 'portfolio_contact_click',
        data: { portfolio_id: portfolio.id, channel },
      },
      portfolio.isOwner,
    );
  }

  // Scroll-depth tracking: fyra trösklar (25/50/75/100%). Skickas bara
  // en gång per session — vi vill se "X procent av besökarna nådde botten",
  // inte spam vid varje wheel-event. SKIPPAS om ägaren är inne.
  useEffect(() => {
    if (portfolio.isOwner) return;
    const fired = new Set<0.25 | 0.5 | 0.75 | 1.0>();
    function onScroll() {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = window.scrollY / docHeight;
      const thresholds: Array<0.25 | 0.5 | 0.75 | 1.0> = [0.25, 0.5, 0.75, 1.0];
      for (const t of thresholds) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          track({
            name: 'portfolio_scroll_depth',
            data: { portfolio_id: portfolio.id, depth: t },
          });
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [portfolio.id, portfolio.isOwner]);

  return (
    <div className="pv-shell">
      {/* Top brand strip */}
      <header className="pv-topbar">
        <a
          href="https://vuelette.com"
          target="_blank"
          rel="noopener"
          className="pv-topbar-brand"
          aria-label="Vuelette"
        >
          <Logo size="md" color="#ededed" />
        </a>

        <div className="pv-topbar-right">
          {header?.brand ? (
            <span className="pv-topbar-brand-mark">{header.brand}</span>
          ) : null}

          {/* Owner-tools: visas bara om inloggad workspace-member */}
          {portfolio.isOwner ? (
            editMode ? (
              <div className="pv-owner-tools">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="pv-owner-btn"
                  aria-label="Cancel"
                >
                  <XIcon className="w-3.5 h-3.5" />
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={() => reorderMutation.mutate()}
                  disabled={reorderMutation.isPending}
                  className="pv-owner-btn pv-owner-btn-primary"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>{reorderMutation.isPending ? 'Saving…' : 'Save order'}</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                className="pv-owner-btn"
                title="Reorder images (drag to reorder)"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span>Edit order</span>
              </button>
            )
          ) : null}
        </div>
      </header>

      {/* Lightbox overlay — öppen när någon bild klickats */}
      {lightboxIndex !== null ? (
        <PortfolioLightbox
          images={allImages}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onImageView={handleImageView}
        />
      ) : null}

      {/* Hero — Variant E Storytelling: 40/60 split */}
      {hasHeader && heroImage ? (
        <section className="pv-hero">
          <div className="pv-hero-info">
            {header?.modelName ? (
              <h1 className="pv-name">{header.modelName}</h1>
            ) : (
              <h1 className="pv-name">{portfolio.name}</h1>
            )}

            {/* Age / location / ethnicity ovanför measurements som en personlig identitets-strof */}
            {(age !== null || header?.location || header?.ethnicity) ? (
              <dl className="pv-bio">
                {age !== null ? (
                  <>
                    <dt>Age</dt>
                    <dd>{age}</dd>
                  </>
                ) : null}
                {header?.location ? (
                  <>
                    <dt>Based</dt>
                    <dd>{header.location}</dd>
                  </>
                ) : null}
                {header?.ethnicity ? (
                  <>
                    <dt>Background</dt>
                    <dd>{header.ethnicity}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}

            {header?.measurements ? (
              <dl className="pv-measurements">
                {header.measurements.height ? (
                  <>
                    <dt>Height</dt>
                    <dd>{header.measurements.height}</dd>
                  </>
                ) : null}
                {header.measurements.bust ? (
                  <>
                    <dt>Bust</dt>
                    <dd>{header.measurements.bust}</dd>
                  </>
                ) : null}
                {header.measurements.waist ? (
                  <>
                    <dt>Waist</dt>
                    <dd>{header.measurements.waist}</dd>
                  </>
                ) : null}
                {header.measurements.hips ? (
                  <>
                    <dt>Hips</dt>
                    <dd>{header.measurements.hips}</dd>
                  </>
                ) : null}
                {header.measurements.shoeSize ? (
                  <>
                    <dt>Shoe</dt>
                    <dd>{header.measurements.shoeSize}</dd>
                  </>
                ) : null}
                {header.measurements.hair ? (
                  <>
                    <dt>Hair</dt>
                    <dd>{header.measurements.hair}</dd>
                  </>
                ) : null}
                {header.measurements.eyes ? (
                  <>
                    <dt>Eyes</dt>
                    <dd>{header.measurements.eyes}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}

            {header?.intro ? (
              <p className="pv-intro">{header.intro}</p>
            ) : null}

            {header?.contact ? (
              <ul className="pv-contact">
                {header.contact.email ? (
                  <li>
                    <a
                      href={`mailto:${header.contact.email}`}
                      onClick={() => handleContactClick('email')}
                    >
                      {header.contact.email}
                    </a>
                  </li>
                ) : null}
                {header.contact.phone ? (
                  <li>
                    <a
                      href={`tel:${header.contact.phone}`}
                      onClick={() => handleContactClick('phone')}
                    >
                      {header.contact.phone}
                    </a>
                  </li>
                ) : null}
                {header.contact.instagram ? (
                  <li>
                    <a
                      href={`https://instagram.com/${header.contact.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener"
                      onClick={() => handleContactClick('instagram')}
                    >
                      @{header.contact.instagram.replace('@', '')}
                    </a>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>

          <div
            className="pv-hero-image"
            onClick={() => openLightbox(heroImage)}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={heroImage.previewUrl}
              alt={heroImage.filename}
              loading="eager"
            />
          </div>
        </section>
      ) : null}

      {/* Primary set — masonry layout */}
      <PortfolioSet
        setName={portfolio.primarySet.name}
        images={primaryOrder}
        kind="primary"
        skipFirstImage={hasHeader}
        editMode={editMode}
        onReorder={setPrimaryOrder}
        onOpenImage={openLightbox}
      />

      {/* Secondary set — masonry layout, smaller */}
      {portfolio.secondarySet ? (
        <PortfolioSet
          setName={portfolio.secondarySet.name}
          images={secondaryOrder}
          kind="secondary"
          editMode={editMode}
          onReorder={setSecondaryOrder}
          onOpenImage={openLightbox}
        />
      ) : null}

      {/* Footer */}
      <footer className="pv-footer">
        <div className="pv-footer-inner">
          <a
            href="https://vuelette.com"
            target="_blank"
            rel="noopener"
            className="pv-footer-brand"
          >
            Delivered by <Logo size="sm" color="#b3b3b3" />
          </a>
        </div>
      </footer>
    </div>
  );
}

// ─── Set rendering ──────────────────────────────────────────────────────

function PortfolioSet({
  setName,
  images,
  kind,
  skipFirstImage = false,
  editMode,
  onReorder,
  onOpenImage,
}: {
  setName: string;
  images: PublicPortfolioImage[];
  kind: 'primary' | 'secondary';
  skipFirstImage?: boolean;
  editMode: boolean;
  onReorder: (newOrder: PublicPortfolioImage[]) => void;
  onOpenImage: (img: PublicPortfolioImage) => void;
}) {
  const visibleImages = skipFirstImage ? images.slice(1) : images;
  const hiddenFirst = skipFirstImage ? images[0] : null;
  const draggingId = useRef<string | null>(null);

  if (visibleImages.length === 0 && !hiddenFirst) return null;

  function handleDragStart(e: React.DragEvent, imgId: string) {
    draggingId.current = imgId;
    e.dataTransfer.effectAllowed = 'move';
    // Sätt en transparent ghost-image så vi inte ser hela bilden floating
    e.dataTransfer.setData('text/plain', imgId);
  }

  function handleDragOver(e: React.DragEvent) {
    if (!editMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e: React.DragEvent, targetImgId: string) {
    if (!editMode) return;
    e.preventDefault();
    const sourceId = draggingId.current;
    draggingId.current = null;
    if (!sourceId || sourceId === targetImgId) return;

    // Räkna ut nya ordningen — flytta source till target's plats
    const newImages = [...images];
    const sourceIdx = newImages.findIndex((i) => i.id === sourceId);
    const targetIdx = newImages.findIndex((i) => i.id === targetImgId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const [moved] = newImages.splice(sourceIdx, 1);
    newImages.splice(targetIdx, 0, moved);
    onReorder(newImages);
  }

  // Primary set får smart row-distribution: balansera till rader av 2 eller 3.
  // Secondary behåller masonry (CSS columns) — den är medvetet tätare/mindre.
  const primaryRows =
    kind === 'primary' ? distributeIntoRows(visibleImages) : null;

  return (
    <section className={`pv-set pv-set-${kind} ${editMode ? 'pv-set-editing' : ''}`}>
      {kind === 'secondary' && setName ? (
        <header className="pv-set-header">
          <span className="pv-set-mono">— {setName}</span>
        </header>
      ) : null}

      {primaryRows ? (
        <div className="pv-rows">
          {primaryRows.map((row, rowIdx) => (
            <div
              key={rowIdx}
              className={`pv-row pv-row-${row.length}`}
            >
              {row.map((img) => (
                <PortfolioImage
                  key={img.id}
                  img={img}
                  editMode={editMode}
                  onOpen={() => onOpenImage(img)}
                  onDragStart={(e) => handleDragStart(e, img.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, img.id)}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className={`pv-grid pv-grid-${kind}`}>
          {visibleImages.map((img) => (
            <PortfolioImage
              key={img.id}
              img={img}
              editMode={editMode}
              onOpen={() => onOpenImage(img)}
              onDragStart={(e) => handleDragStart(e, img.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, img.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Row distribution ───────────────────────────────────────────────────
//
// Givet N bilder, returnera en lista av rader med 2 eller 3 bilder per rad
// (eller 1 om N=1) så att layouten blir balanserad. Föredra rader om 3,
// men använd 2:or där det behövs för att täcka resten.
//
// Exempel:
//   1 → [1]
//   2 → [2]
//   3 → [3]
//   4 → [2, 2]
//   5 → [2, 3]
//   6 → [3, 3]
//   7 → [2, 2, 3]
//   8 → [2, 3, 3]
//   9 → [3, 3, 3]
//  10 → [2, 2, 3, 3]
function distributeIntoRows<T>(items: T[]): T[][] {
  const n = items.length;
  if (n === 0) return [];
  if (n === 1) return [items];

  // Hitta antal 2:or och 3:or: 2a + 3b = n, minimera a, max b
  let twos: number;
  switch (n % 3) {
    case 0:
      twos = 0;
      break;
    case 1:
      // n = 3k+1 → behöver två 2:or (4) + resten 3:or
      twos = 2;
      break;
    case 2:
    default:
      // n = 3k+2 → en 2:a + resten 3:or
      twos = 1;
      break;
  }
  const threes = (n - twos * 2) / 3;

  const rows: T[][] = [];
  let cursor = 0;
  // Sätt 2:orna först — då hamnar de övre raderna mer "luftiga" och
  // 3:orna nedanför, vilket känns naturligt när man scrollar.
  for (let i = 0; i < twos; i++) {
    rows.push(items.slice(cursor, cursor + 2));
    cursor += 2;
  }
  for (let i = 0; i < threes; i++) {
    rows.push(items.slice(cursor, cursor + 3));
    cursor += 3;
  }
  return rows;
}

function PortfolioImage({
  img,
  editMode,
  onOpen,
  onDragStart,
  onDragOver,
  onDrop,
}: {
  img: PublicPortfolioImage;
  editMode: boolean;
  onOpen: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <figure
      className={`pv-frame ${editMode ? 'pv-frame-editing' : ''}`}
      onClick={editMode ? undefined : onOpen}
      draggable={editMode}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <img src={img.previewUrl} alt={img.filename} loading="lazy" draggable={false} />
      {editMode ? (
        <div className="pv-frame-grip" aria-hidden>
          <GripVertical />
        </div>
      ) : null}
    </figure>
  );
}
